/**
 * account_c7.js — Commerce7 Account / Login Optional Feature (Valle della Pace)
 *
 * PURPOSE:
 *   The Commerce7 counterpart to features/optional/account.js (Shopify). C7
 *   login is IN-WIDGET (email/password → customer token), not external URLs.
 *   This feature:
 *     - shows the in-chat login form on first open when logged out (old Valle
 *       addWelcomeMessageAndButtons → showLoginForm, script.js 1683),
 *     - greets logged-in shoppers ("Ciao {first}! Welcome back to our winery."),
 *     - handles the header account button (ws-account-open),
 *     - owns the customer token + customer id used by the cart/checkout flow.
 *
 * TOKEN PERSISTENCE:
 *   The old FE stored a `customerToken` cookie + reloaded the host page to
 *   /profile/. The embeddable widget can't reload the host, so the token lives
 *   in localStorage (ws_c7token_<tenantId>) + state.user, and the greeting is
 *   shown in-chat. state.user = { customerToken, customerId, profile }.
 *
 * EVENTS:
 *   in:  ws-toggle {isOpen}, ws-account-open, ws-login-submit {email,password},
 *        ws-login-skip, ws-logout, ws-prompt-select {value}
 *   out: ws-login-error {message, field}
 *
 * REFERENCE (old code):
 *   valledellapace/script.js onLogin (3523), showLoginForm (1765),
 *   getCustomerDetails (3448), loginPrompts (189), handleLogoutClick (3484).
 *
 * EXPORTS:
 *   init() — subscribes to account events; idempotent.
 */

import * as api from "../../core/api.js";
import { on, emit, getState, setState, addMessage } from "../../core/state.js";
import { getTenantId, isMembershipEnabled } from "../../core/configAccessors.js";
import { endSession } from "../../core/session.js";
import { error } from "../../core/env.js";

const keyFor = (tenantId) => `ws_c7token_${tenantId || "default"}`;

// Post-login prompts (old loginPrompts, script.js 189). "View membership
// details" is a sentinel (__view_membership__, handled by club_c7.js) so it
// gets a deterministic membership summary instead of an LLM chat query —
// only offered where the club/membership feature is actually on.
function loginPrompts() {
  const items = [
    { label: "View cart", value: "Show me my cart", icon: "bolt" },
    { label: "View order history", value: "Show me my past orders", icon: "bolt" },
  ];
  if (isMembershipEnabled()) {
    items.push({ label: "View membership details", value: "__view_membership__", icon: "bolt" });
  }
  return items;
}

function loadToken() {
  try {
    return localStorage.getItem(keyFor(getTenantId())) || null;
  } catch {
    return null;
  }
}

function persistToken(token) {
  setState({ user: { ...getState().user, customerToken: token } });
  try {
    if (token) localStorage.setItem(keyFor(getTenantId()), token);
    else localStorage.removeItem(keyFor(getTenantId()));
  } catch {
    /* storage blocked — token stays in memory for this page only */
  }
}

export function getToken() {
  return getState().user?.customerToken || loadToken();
}

function showText(text, { isFirstResponse = true } = {}) {
  addMessage({ role: "bot", kind: "text", isFirstResponse, data: { text } });
}

function showLoginForm() {
  showText("Log in for personalized wine picks and a better experience.", {
    isFirstResponse: false,
  });
  addMessage({ role: "bot", kind: "login-form", isFirstResponse: false, data: {} });
}

// Resolve the C7 customer for a token (id + firstName). Returns the profile or null.
async function resolveCustomer(token) {
  try {
    const profile = await api.c7GetCustomerSelf(token);
    setState({
      user: {
        ...getState().user,
        customerToken: token,
        customerId: profile?.id || profile?.customerId || "",
        profile: profile || null,
      },
    });
    return profile;
  } catch (e) {
    error("[account_c7] customer self failed", e);
    return null;
  }
}

function greet(profile, { isFirstResponse = true } = {}) {
  const first = profile?.firstName || profile?.first_name || "";
  // Wording matches the BE's get_commerce7_login_message (utils.py 181).
  showText(first ? `Ciao ${first}! Welcome back to our winery.` : "Welcome back to our winery!", { isFirstResponse });
  addMessage({ role: "bot", kind: "prompts", isFirstResponse: false, data: { items: loginPrompts() } });
}

async function greetIfLoggedIn() {
  const token = getToken();
  if (!token) return false;
  let profile = getState().user?.profile;
  if (!profile) profile = await resolveCustomer(token);
  // Welcome-open path: do not scroll (banner must stay visible).
  greet(profile, { isFirstResponse: false });
  return true;
}

async function handleLoginSubmit({ email, password } = {}) {
  try {
    const sessionId = getState().session?.id || "";
    const data = await api.c7Login({ email, password, sessionId });
    const token = data?.token;
    if (!token) {
      emit("ws-login-error", { message: "Login failed. Please try again." });
      return;
    }
    persistToken(token);
    // Resolve the customer, signal the form to remove itself (shadow-DOM safe —
    // the form owns its element ref), then greet in-chat. The old FE reloaded
    // the host page to /profile/ instead, which an embedded widget can't do.
    const profile = await resolveCustomer(token);
    emit("ws-login-success");
    greet(profile);
  } catch (e) {
    error("[account_c7] login failed", e);
    // apiFetch attaches the HTTP status to err.status; surface 401 specially.
    if (e?.status === 401) emit("ws-login-error", { message: "Invalid email or password" });
    else if (e?.status === 502) emit("ws-login-error", { message: "Server error. Please try again." });
    else emit("ws-login-error", { message: "Something went wrong. Please try again." });
  }
}

function handleLogout() {
  persistToken(null);
  setState({ user: { ...getState().user, customerToken: null, customerId: "", profile: null } });
  showText("You've been logged out.");
  // Logout ends the conversation handle too — the next open/message starts a
  // brand-new session instead of resuming this now-signed-out one.
  endSession(getTenantId());
}

// Header account button: greet (logged in) or show the login form (logged out).
async function handleAccountOpen() {
  const greeted = await greetIfLoggedIn();
  if (greeted) {
    addMessage({ role: "bot", kind: "prompts", data: { items: [{ label: "Logout", value: "__logout__", icon: "bolt" }] } });
    return;
  }
  showLoginForm();
}

function handlePrompt({ value } = {}) {
  if (value === "__logout__") handleLogout();
}

let started = false;

export async function init() {
  if (started) return;
  started = true;

  // Whenever the welcome + prompts are seeded (first open AND on refresh), greet
  // returning (logged-in) customers, else show the login form below the welcome
  // — faithful to the old addWelcomeMessageAndButtons(), which rendered the
  // welcome then ran `if (!customerToken) showLoginForm(); showLoggedInUserMessage();`
  // inline every time (script.js 1681-1702). Using the welcome-seeded hook (not a
  // one-shot ws-toggle guard) means it re-shows on refresh and can't miss a
  // toggle that fired before this feature subscribed.
  on("ws-welcome-seeded", async () => {
    const greeted = await greetIfLoggedIn();
    if (!greeted) showLoginForm();
  });

  on("ws-account-open", handleAccountOpen);
  on("ws-login-submit", handleLoginSubmit);
  on("ws-logout", handleLogout);
  on("ws-prompt-select", handlePrompt);
}
